import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DonationPI from '../public/DonationPI';
import DonationInfo from '../public/DonationInfo';
import InternalDonationPayment from './InternalDonationPayment';
import PageHeader from '../../../Components/PageHeader';
import { getDonationDetail } from '../../../endpoints/donations';
import {
  createInternalDonation,
  updateInternalDonationDonor,
  createInternalDonationLine,
  removeInternalDonationLine,
  removeInternalDonationNotification,
} from '../../../endpoints/donations';
import { useApiGet } from '../../../utils/httpClient';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';

const steps = ['Donor', 'Donation lines', 'Payment'];

const saveInternalDonor = (data) => {
  if (data.donation_id) {
    const { donation_id, ...rest } = data;
    return updateInternalDonationDonor({ id: donation_id, ...rest });
  }
  return createInternalDonation(data);
};

export default function InternalDonationNew() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [donationId, setDonationId] = useState(null);
  const [donationObject, setDonationObject] = useState(null);

  const { data: permissions } = useApiGet(
    ['my-permissions-internal-donation'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );

  const canManage = hasUserApplicationPermission(
    permissions,
    'donations:manage'
  );

  const { data: donationData, refetch } = useApiGet(
    ['internal-donation-detail', donationId],
    getDonationDetail,
    { enabled: !!donationId && canManage },
    { id: donationId }
  );

  useEffect(() => {
    if (donationData) {
      setDonationObject(donationData);
    }
  }, [donationData]);

  useEffect(() => {
    if (permissions && !canManage) {
      navigate('/internal/donations', { replace: true });
    }
  }, [permissions, canManage, navigate]);

  const handleNext = useCallback(() => {
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((s) => Math.max(s - 1, 0));
  }, []);

  const saveDonationLine = useCallback(
    (payload) =>
      createInternalDonationLine({
        id: donationId,
        ...payload,
      }),
    [donationId]
  );

  const removeLine = useCallback(
    (data) =>
      removeInternalDonationLine({
        donationId,
        lineId: data.id,
      }),
    [donationId]
  );

  const removeNotification = useCallback(
    (data) =>
      removeInternalDonationNotification({
        donationId,
        notificationId: data.id,
      }),
    [donationId]
  );

  if (permissions && !canManage) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <PageHeader title="New donation" />
      <Paper sx={{ p: 3, mt: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <DonationPI
            handleNext={handleNext}
            setDonationId={setDonationId}
            refetch={refetch}
            donationObject={donationObject}
            donationId={donationId}
            saveDonation={saveInternalDonor}
            emailOptional
            allowMemberLink
            allowOrganizationDonor
            addressOptional
          />
        )}

        {activeStep === 1 && donationId && (
          <DonationInfo
            handleNext={handleNext}
            handleBack={handleBack}
            donationId={donationId}
            donationObject={donationObject}
            refetch={refetch}
            saveDonationLine={saveDonationLine}
            removeDonationLineFn={removeLine}
            removeDonationNotificationFn={removeNotification}
            internalLineDefaults
          />
        )}

        {activeStep === 2 && donationId && (
          <InternalDonationPayment
            donationId={donationId}
            donationObject={donationObject}
            handleBack={handleBack}
          />
        )}

        {activeStep === 1 && !donationId && (
          <Typography color="text.secondary">
            Complete donor information first.
          </Typography>
        )}
      </Paper>
    </Container>
  );
}
